import { MinusCircleOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { ServiceExtraTypeSelect } from '@components';
import { L } from '@lib';
import { Utils } from '@utils';
import { Button, Col, Input, InputNumber, Popconfirm, Row, Tag, Tooltip, Typography } from 'antd';
import React from 'react';
import { IServiceExtraPresentation } from './ServiceExtraForm.props';

const { Text } = Typography;
const { TextArea } = Input;

const numericOnly = (e: React.KeyboardEvent) => {
  const allowed = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End', 'Enter'];
  if (allowed.includes(e.key) || e.ctrlKey || e.metaKey) return;
  if (!/^[0-9.,]$/.test(e.key)) e.preventDefault();
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#8c8c8c',
  textTransform: 'uppercase',
};

export const ServiceExtraMobile: React.FC<IServiceExtraPresentation> = ({
  extra,
  disabled,
  showStock,
  showPricing,
  showDiscount,
  showTotals,
  price,
  costPrice,
  margin,
  quantity,
  discount,
  subTotal,
  vatAmount,
  total,
  handlePriceChange,
  handleCostPriceChange,
  handleMarginChange,
  handleQuantityChange,
  handleDiscountChange,
  handleQuantityBlur,
  handleNameChange,
  handleNumberChange,
  handleReferenceChange,
  handleTypeChange,
  handleRemove,
  onAddToPurchaseOrder,
}) => (
  <div style={{ padding: '8px 0', borderBottom: '1px solid #f5f5f5', marginBottom: 8 }}>
    <Row gutter={[12, 8]}>
      {/* Name gives up a little width so the two icon actions sit side by side on its row. */}
      <Col span={17}>
        <Text style={labelStyle}>Name</Text>
        <TextArea
          placeholder="Enter name"
          rows={1}
          value={extra.name}
          disabled={disabled}
          autoSize={{ minRows: 1 }}
          onChange={(e: any) => handleNameChange(e.currentTarget.value)}
        />
        {extra.required && (!extra.name || !extra.name.trim().length) && (
          <Text style={{ color: 'red' }}>{L('Please enter name')}</Text>
        )}
      </Col>
      <Col
        span={7}
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
          paddingBottom: 4,
          gap: 6,
        }}
      >
        {onAddToPurchaseOrder && (
          <Button
            icon={<ShoppingCartOutlined />}
            disabled={disabled}
            size="small"
            onClick={onAddToPurchaseOrder}
          />
        )}
        <Popconfirm
          title="Remove this part?"
          okText="Remove"
          okButtonProps={{ danger: true }}
          onConfirm={handleRemove}
          disabled={disabled}
        >
          <Button icon={<MinusCircleOutlined />} size="small" danger disabled={disabled} />
        </Popconfirm>
      </Col>
      <Col span={12}>
        <Text style={labelStyle}>Part #</Text>
        <Input
          placeholder="From invoice"
          value={extra.number || ''}
          disabled={disabled}
          onChange={(e: any) => handleNumberChange(e.currentTarget.value)}
        />
      </Col>
      <Col span={12}>
        <Text style={labelStyle}>Reference</Text>
        <Input
          placeholder="Stock code"
          value={extra.reference || ''}
          disabled={disabled}
          onChange={(e: any) => handleReferenceChange(e.currentTarget.value)}
        />
      </Col>
      {/* Type alone, then quantity + discount, then cost / margin / sale as three equal thirds. */}
      <Col span={24}>
        <Text style={labelStyle}>Type</Text>
        <ServiceExtraTypeSelect
          value={extra.type}
          disabled={disabled}
          onChange={handleTypeChange}
        />
      </Col>
      <Col span={12}>
        {(() => {
          const availableStock = (extra as any).availableStock as number | undefined;
          const hasStockRef = showStock && typeof availableStock === 'number' && !!extra.inventoryId;
          const overStock = hasStockRef && (quantity ?? 0) > availableStock!;
          const stockTooltip = hasStockRef
            ? overStock
              ? `Only ${availableStock} in stock`
              : `${availableStock} in stock`
            : undefined;
          return (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Text style={labelStyle}>Quantity</Text>
                {hasStockRef && (
                  <Tooltip title={stockTooltip}>
                    <span
                      style={{
                        fontSize: 10,
                        lineHeight: '14px',
                        padding: '0 6px',
                        borderRadius: 8,
                        background: overStock ? '#fff1f0' : '#f0f5ff',
                        color: overStock ? '#ff4d4f' : '#2f54eb',
                        border: `1px solid ${overStock ? '#ffccc7' : '#d6e4ff'}`,
                        fontWeight: 600,
                        cursor: 'help',
                      }}
                    >
                      {availableStock}
                    </span>
                  </Tooltip>
                )}
              </div>
              <InputNumber
                min={0}
                step={0.01}
                precision={2}
                value={quantity}
                disabled={disabled}
                onChange={handleQuantityChange}
                onBlur={handleQuantityBlur}
                onKeyDown={numericOnly}
                status={overStock ? 'error' : undefined}
                style={{ width: '100%' }}
              />
            </>
          );
        })()}
      </Col>
      {showDiscount && (
        <Col span={12}>
          <Text style={labelStyle}>Discount (%)</Text>
          <InputNumber
            min={0}
            step={0.01}
            precision={2}
            value={discount}
            disabled={disabled}
            onChange={handleDiscountChange}
            onKeyDown={numericOnly}
            style={{ width: '100%' }}
          />
        </Col>
      )}
      {showPricing && (
        <>
          <Col span={8}>
            <Text style={labelStyle}>Cost (£)</Text>
            <InputNumber
              min={0}
              step={0.01}
              precision={2}
              value={costPrice}
              disabled={disabled}
              onChange={handleCostPriceChange}
              onKeyDown={numericOnly}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={8}>
            <Text style={labelStyle}>Margin (%)</Text>
            <InputNumber
              min={0}
              step={0.01}
              precision={2}
              value={margin}
              disabled={disabled}
              onChange={handleMarginChange}
              onKeyDown={numericOnly}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={8}>
            <Text style={labelStyle}>Sale (£)</Text>
            <InputNumber
              min={0}
              step={0.01}
              precision={2}
              value={price}
              disabled={disabled}
              onChange={handlePriceChange}
              onKeyDown={numericOnly}
              style={{ width: '100%' }}
            />
          </Col>
        </>
      )}
      {showTotals && (
        <Col span={24}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 14,
              marginTop: 4,
              alignItems: 'center',
            }}
          >
              <span style={{ fontSize: 13, color: '#8c8c8c' }}>
                Sub Total: <strong style={{ color: '#555' }}>{Utils.formatCurrency(subTotal)}</strong>
              </span>
              <span style={{ color: '#ddd' }}>|</span>
              <span style={{ fontSize: 13, color: '#8c8c8c' }}>
                VAT: <strong style={{ color: '#555' }}>{Utils.formatCurrency(vatAmount)}</strong>
              </span>
              <span style={{ color: '#ddd' }}>|</span>
            <Tag
              color="blue"
              style={{ fontSize: 14, fontWeight: 700, padding: '2px 8px', margin: 0 }}
            >
              {Utils.formatCurrency(total)}
            </Tag>
          </div>
        </Col>
      )}
    </Row>
  </div>
);
